/**
 * Utility functions for platform detection
 */

/**
 * Checks if the app is running in a Capacitor environment (mobile app)
 */
export const isCapacitorApp = (): boolean => {
  return typeof (window as any).Capacitor !== 'undefined';
};

/**
 * Checks if the app is running on Android
 */
export const isAndroid = (): boolean => {
  return isCapacitorApp() && (window as any).Capacitor.getPlatform() === 'android';
};

/**
 * Checks if the app is running on iOS
 */
export const isIOS = (): boolean => {
  return isCapacitorApp() && (window as any).Capacitor.getPlatform() === 'ios';
};

/**
 * Checks if the app is running on a mobile device (either in a browser or native app)
 */
export const isMobileDevice = (): boolean => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  return mobileRegex.test(userAgent.toLowerCase()) || isCapacitorApp();
};

/**
 * Gets the current platform
 * @returns 'android' | 'ios' | 'web'
 */
export const getPlatform = (): 'android' | 'ios' | 'web' => {
  if (isAndroid()) return 'android';
  if (isIOS()) return 'ios';
  return 'web';
};

/**
 * Ensures the language code is properly formatted for the current platform
 * Some mobile WebViews have issues with certain language codes
 * @param language The language code to normalize
 * @returns The normalized language code
 */
export const normalizeLanguageCode = (language: string): string => {
  // If we're in a mobile app, ensure we're using simple language codes
  if (isCapacitorApp()) {
    // For Yiddish, ensure we're using 'yi' and not 'yi-XX'
    if (language.startsWith('yi')) return 'yi';
    // For Hebrew, ensure we're using 'he' and not 'he-XX'
    if (language.startsWith('he')) return 'he';
    // For other languages, just use the base language code
    return language.split('-')[0];
  }
  
  // On web, return the original language code
  return language;
}; 